import UserModel                 from '@models/fondations/UserModel';
import ApiCollection             from '@widesk-core/models/ApiCollection';
import { action }                from 'mobx';
import { computed }              from 'mobx';
import { observable }            from 'mobx';
import { makeObservable }        from 'mobx';
import WebsiteEntertainmentModel from '@models/website/WebsiteEntertainmentModel';
import WebsiteLanguageModel      from '@models/website/WebsiteLanguageModel';
import LanguageModel             from '@models/website/LanguageModel';
import ContentToPublishModel     from '@models/website/ContentToPublishModel';
import WebsiteModel              from '@models/website/WebsiteModel';
import dayjs                     from 'dayjs';

const LOCALSTORAGE_WEBSITE_ENTERTAINMENT_ID_KEY = 'website_entertainment_id';

class AppStore {
	public readonly user = new UserModel();
	public collWebsiteEntertainment = new ApiCollection(WebsiteEntertainmentModel);
	public collWebsiteLanguage = new ApiCollection(WebsiteLanguageModel);
	public collLanguage = new ApiCollection(LanguageModel);
	public collContentToPublish = new ApiCollection(ContentToPublishModel);

	@observable
	private _configurationMenuOpened: boolean = false;

	@observable
	private _isMenuCollapsed: boolean = false;

	@observable
	private _websiteEntertainmentId: id | null = null;

	constructor() {
		makeObservable(this);
	}

	public async load() {
		await Promise.all([
			(async () => {
				await this.collWebsiteEntertainment
					.setFilter('tags[jts:registry:service:website][display_in_sidebar]', '1')
					.setSort('id', 'desc')
					.setIncludeFiles(true)
					.list();

				const websiteIds = this.collWebsiteEntertainment.map(we => we.getId('website'));

				await Promise.all([
					this.collWebsiteEntertainment.whenIsLoaded(we => [
						we.entertainment,
						we.website,
					]),

					this.collWebsiteLanguage.listBy(websiteIds, 'website').then(async () => {
						await this.collWebsiteLanguage.whenIsLoaded(m => m.language);
					}),
				]);
			})(),

			this.collLanguage.list(),
		]);

		const cachedWebsiteEntertainmentId = localStorage.getItem(LOCALSTORAGE_WEBSITE_ENTERTAINMENT_ID_KEY);
		if (cachedWebsiteEntertainmentId) {
			this.changeWebsiteEntertainment(cachedWebsiteEntertainmentId);
			// navigateWithWebsiteEntertainment('/home');
		}

		void this.collContentToPublish
			.setRequiredFilter('contentToPublishStatus', '1') // status ENABLED
			.list();
	}

	@action resetWebsiteEntertainmentId() {
		this._websiteEntertainmentId = null;
		localStorage.removeItem(LOCALSTORAGE_WEBSITE_ENTERTAINMENT_ID_KEY);
	}

	@action changeWebsiteEntertainment(id: id) {
		this._websiteEntertainmentId = id;
		localStorage.setItem(LOCALSTORAGE_WEBSITE_ENTERTAINMENT_ID_KEY, id);
	}

	@computed get authorizedLanguageKeys(): LanguageKey[] {
		return this.websiteLanguages.map(l => l.language.locale);
	}

	@computed get mandatoryLanguageKeys(): LanguageKey[] {
		const languageIds = this.websiteLanguages
			.filter(wl => wl.mandatory)
			.map(wl => wl.getId('language'));

		return this.collLanguage
			.filter(l => languageIds.includes(l.id))
			.map(l => l.locale);
	}

	@computed get websiteEntertainment(): WebsiteEntertainmentModel | null {
		return this.collWebsiteEntertainment.getById(this._websiteEntertainmentId || 0);
	}

	@computed get website(): WebsiteModel | null {
		return this.collWebsiteEntertainment.getById(this._websiteEntertainmentId || 0)?.website || null;
	}

	@computed get websiteLanguages(): WebsiteLanguageModel[] {
		const websiteId = this.websiteEntertainment?.getId('website');
		return websiteId
			? this.collWebsiteLanguage.filter(wl => wl.getId('website') === websiteId)
			: [];
	}

	@computed get entertainmentDateStart(): Dayjs {
		return this.websiteEntertainment?.entertainment.dateStart || dayjs();
	}

	@computed get entertainmentDateEnd(): Dayjs {
		return this.websiteEntertainment?.entertainment.dateEnd || dayjs();
	}

	@computed get entertainmentDates(): [Dayjs, Dayjs] {
		return [this.entertainmentDateStart, this.entertainmentDateEnd];
	}

	public clear() {
		this.user.clear();
		this.resetWebsiteEntertainmentId();
		this.setConfigurationMenuOpened(false);
	}

	@action setMenuCollapsed(isMenuCollapsed: boolean) {
		this._isMenuCollapsed = isMenuCollapsed;
	}

	@computed get isMenuCollapsed() {
		return this._isMenuCollapsed;
	}

	@action setConfigurationMenuOpened(isConfigurationMenuOpened: boolean) {
		this._configurationMenuOpened = isConfigurationMenuOpened;
	}

	@computed get isConfigurationMenuOpened() {
		return this._configurationMenuOpened;
	}

	@computed get isAppMenuOpened() {
		return this.isConfigurationMenuOpened || this.websiteEntertainment !== null;
	}
}

export default new AppStore();
